import { ChevronDown } from 'lucide-react';

export interface FaqItem {
  question: string;
  answer: string;
}

/** Builds the FAQPage JSON-LD for a page's <SEO jsonLd={...}>. The visible section
 *  below must render the SAME items — Google requires the Q&A to exist on-page. */
export function faqSchema(faqs: FaqItem[]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question,
      acceptedAnswer: { '@type': 'Answer', text: f.answer },
    })),
  };
}

/** FAQ accordion on native <details>/<summary>: answers live in the DOM even when
 *  collapsed, so the prerenderer bakes them into the static HTML for Google
 *  (Radix Accordion unmounts closed content — invisible to crawlers). */
export function FaqSection({ title, faqs }: { title?: string; faqs: FaqItem[] }) {
  return (
    <div className="pb-16 px-4 sm:px-6 lg:px-8 xl:px-12">
      <div className="max-w-3xl mx-auto">
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold tracking-[-0.03em] text-text-primary text-center mb-10">
          {title ?? 'Preguntas frecuentes'}
        </h2>
        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <details
              key={index}
              className="group bg-white rounded-[24px] shadow-card overflow-hidden"
            >
              <summary className="flex items-center justify-between gap-4 cursor-pointer list-none px-6 py-5 [&::-webkit-details-marker]:hidden">
                <h3 className="font-heading text-base sm:text-lg font-semibold text-text-primary">
                  {faq.question}
                </h3>
                <ChevronDown className="w-5 h-5 text-brand flex-shrink-0 transition-transform group-open:rotate-180" />
              </summary>
              <p className="px-6 pb-6 text-text-secondary text-sm sm:text-base leading-relaxed">
                {faq.answer}
              </p>
            </details>
          ))}
        </div>
      </div>
    </div>
  );
}
